/**
 * DateSynchronizationService
 * 
 * Manages the synchronization between different date fields used across
 * the College Journey module to ensure consistency between Calendar and Epic views.
 * 
 * Date Fields:
 * - scheduled_date: When the student plans to work on the task (Calendar primary)
 * - due_date: The actual deadline for the task (Epic primary)
 * - completion_date: When the task was actually completed
 */

import { format, parseISO, differenceInDays, addDays, isBefore, isAfter, startOfDay } from 'date-fns';

class DateSynchronizationService {
  /**
   * Default lead time for scheduling work before due dates (in days)
   */
  static DEFAULT_LEAD_TIME = 3;

  /**
   * Get synchronized date object with both scheduled and due dates
   * Ensures both Calendar and Epic views have the dates they need
   */
  static getSynchronizedDates(sourceField, sourceValue, taskType = 'general') {
    let scheduledDate, dueDate;
    const dateValue = typeof sourceValue === 'string' ? sourceValue : format(sourceValue, 'yyyy-MM-dd');

    switch (sourceField) {
      case 'due_date':
        dueDate = dateValue;
        // Initially set scheduled_date to match due_date (student can modify later)
        scheduledDate = dateValue;
        break;

      case 'scheduled_date':
        scheduledDate = dateValue;
        // Don't modify due_date when scheduled_date is updated (due_date is immutable)
        // Only set if no due_date exists
        dueDate = null;
        break;

      default:
        // Both dates are the same if not specified
        scheduledDate = dateValue;
        dueDate = dateValue;
        break;
    }

    return {
      scheduled_date: scheduledDate,
      due_date: dueDate
    };
  }

  /**
   * Calculate when to schedule work on a task based on its due date
   */
  static calculateScheduledDate(dueDate, taskType) {
    if (!dueDate) return null;

    const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
    let leadTimeDays = this.DEFAULT_LEAD_TIME;

    // Adjust lead time based on task type
    switch (taskType) {
      case 'application':
      case 'essay':
        leadTimeDays = 7; // Start a week before
        break;
      case 'test':
      case 'interview':
        leadTimeDays = 14; // Two weeks prep time
        break;
      case 'financial_aid':
      case 'fafsa':
        leadTimeDays = 5; // Five days before
        break;
      case 'recommendation':
        leadTimeDays = 21; // Three weeks notice
        break;
      default:
        leadTimeDays = this.DEFAULT_LEAD_TIME;
    }

    const scheduledDateObj = addDays(dueDateObj, -leadTimeDays);
    return format(scheduledDateObj, 'yyyy-MM-dd');
  }

  /**
   * Estimate a due date when only scheduled date is known
   */
  static estimateDueDate(scheduledDate, taskType) {
    if (!scheduledDate) return null;

    const scheduledDateObj = typeof scheduledDate === 'string' ? parseISO(scheduledDate) : scheduledDate;
    let bufferDays = this.DEFAULT_LEAD_TIME;

    // Add buffer based on task type
    switch (taskType) {
      case 'application':
      case 'essay':
        bufferDays = 7;
        break;
      case 'research':
      case 'visit':
        bufferDays = 30; // Flexible deadline
        break;
      default:
        bufferDays = this.DEFAULT_LEAD_TIME;
    }

    const dueDateObj = addDays(scheduledDateObj, bufferDays);
    return format(dueDateObj, 'yyyy-MM-dd');
  }

  /**
   * Update task with synchronized date fields
   */
  static async updateTaskDates(supabase, taskId, sourceField, sourceValue, taskType = 'general') {
    const syncedDates = this.getSynchronizedDates(sourceField, sourceValue, taskType);
    
    const { data, error } = await supabase
      .from('timeline_items')
      .update({
        ...syncedDates,
        date_updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .select()
      .single();

    if (error) {
      console.error('Error updating synchronized dates:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get urgency level based on due date
   */
  static getUrgencyLevel(dueDate) {
    if (!dueDate) return 'normal';

    const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
    const today = startOfDay(new Date());
    const daysUntilDue = differenceInDays(dueDateObj, today);

    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 1) return 'critical';
    if (daysUntilDue <= 3) return 'urgent';
    if (daysUntilDue <= 7) return 'soon';
    if (daysUntilDue <= 14) return 'upcoming';
    return 'normal';
  }

  /**
   * Get display information for urgency level
   */
  static getUrgencyDisplay(urgencyLevel) {
    const displays = {
      'overdue': { label: 'Overdue', color: 'red', icon: 'AlertTriangle' },
      'critical': { label: 'Due Today/Tomorrow', color: 'orange', icon: 'AlertCircle' },
      'urgent': { label: 'Due in 3 days', color: 'amber', icon: 'Clock' },
      'soon': { label: 'Due this week', color: 'yellow', icon: 'Calendar' },
      'upcoming': { label: 'Due in 2 weeks', color: 'azure', icon: 'Calendar' },
      'normal': { label: 'Future', color: 'gray', icon: 'Calendar' }
    };
    return displays[urgencyLevel] || displays['normal'];
  }

  /**
   * Format date for display
   */
  static formatDateForDisplay(date, showYear = true) {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const today = startOfDay(new Date());
    const daysUntil = differenceInDays(dateObj, today);

    // Special formatting for near dates
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil === -1) return 'Yesterday';
    if (daysUntil > 0 && daysUntil <= 7) {
      return format(dateObj, 'EEEE'); // Day name for this week
    }

    // Standard formatting
    if (showYear) {
      return format(dateObj, 'MMM d, yyyy');
    } else {
      return format(dateObj, 'MMM d');
    }
  }

  /**
   * Check if a task needs rescheduling based on its dates and status
   */
  static needsRescheduling(task) {
    if (!task.scheduled_date || task.status === 'completed' || task.workflow_phase === 'complete') {
      return false;
    }

    const scheduledDateObj = parseISO(task.scheduled_date);
    const today = startOfDay(new Date());

    // Task is overdue for scheduling
    if (isBefore(scheduledDateObj, today) && !this.isTaskActive(task)) {
      return true;
    }

    // Task has a due date that's before scheduled date (conflict)
    if (task.due_date) {
      const dueDateObj = parseISO(task.due_date);
      if (isBefore(dueDateObj, scheduledDateObj)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper to check if task is active (imported from StatusMappingService logic)
   */
  static isTaskActive(task) {
    const activeWorkflowPhases = ['in_progress', 'review'];
    const activeStatuses = ['in_progress'];
    
    return (
      (task.workflow_phase && activeWorkflowPhases.includes(task.workflow_phase)) ||
      (task.status && activeStatuses.includes(task.status))
    );
  }

  /**
   * Get suggested reschedule date for a task
   */
  static getSuggestedRescheduleDate(task) {
    const today = startOfDay(new Date());
    
    // If task has a due date, schedule based on that
    if (task.due_date) {
      return this.calculateScheduledDate(task.due_date, task.category || 'general');
    }
    
    // Otherwise, suggest scheduling for 3 days from now
    return format(addDays(today, 3), 'yyyy-MM-dd');
  }

  /**
   * Validate date consistency
   */
  static validateDates(scheduledDate, dueDate) {
    const errors = [];

    if (scheduledDate && dueDate) {
      const scheduledDateObj = typeof scheduledDate === 'string' ? parseISO(scheduledDate) : scheduledDate;
      const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;

      if (isAfter(scheduledDateObj, dueDateObj)) {
        errors.push('Scheduled date should be before the due date');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default DateSynchronizationService;